import { useContext } from 'react';
import { Badge, useTheme, Tooltip, Typography, styled } from '@mui/material';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import IconButtonWhiteBg from '../../common/IconButtonWhiteBg';
import { useParams, Link, useNavigate } from 'react-router';

import CollapsedTitleWithTooltip from '../../common/CollapsedTitleWithTooltip';
import { AppContext } from '../../../App';
import FullDocumentView from './FullDocumentView';
import { DocumentDetailsParamTypes } from '../../../pages/DocumentDetailsPage';
import { IDocumentUnscoped } from '../../../types/my-types';
import DownloadDocument from '../../common/DownloadDocument';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { splitCaution } from '../split/splitEligibility';
import { getDuplicateLineage } from '../documentUtils';
import RerenderDocumentPages from './RerenderDocumentPages';

import {
  DivVerticalDividerLarge,
  DivVerticalDividerSmall,
  TypographyPropertiesTitleStyled,
  TypographyPropertiesDataStyled,
} from '../../common/StyledComponents';

interface IDocumentDetailsRightProps {
  documentData: IDocumentUnscoped;
}

const DocumentDetailsRight = ({ documentData }: IDocumentDetailsRightProps) => {
  const theme = useTheme();
  const { dpid, documentId } = useParams<DocumentDetailsParamTypes>();
  const { setToast } = useContext(AppContext);
  const navigate = useNavigate();

  const createdDate = new Date(documentData.created_at);

  // Duplicate lineage from the relationship fields (replaces hash-based grouping).
  const { children: dupChildren, showDuplicates } = getDuplicateLineage(documentData);

  return (
    <div
      style={{
        backgroundColor: theme.palette.custom.pageBackground,
        height: 'calc(100vh - 64px - 20px)',
        padding: '20px 20px 0',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            sx={{
              marginRight: '15px',
              fontSize: '14px',
              fontWeight: 'bold',
              maxWidth: '400px',
              cursor: 'pointer',
            }}
            onClick={() => {
              navigator.clipboard.writeText(documentData.label);
              setToast({
                open: true,
                message: 'Copied!',
                variant: 'success',
              });
            }}
          >
            <CollapsedTitleWithTooltip
              title={documentData.label}
              charsFirstAndLast={105}
              clickToCopy={true}
              cypressId="span-document-details-label"
            />
          </Typography>
        </div>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div>
            <TypographyPropertiesTitleStyled>DOCUMENT ID</TypographyPropertiesTitleStyled>
            <TypographyPropertiesDataStyled>
              <span
                data-cy={`span-document-id`}
                onClick={() => {
                  navigator.clipboard.writeText(documentData.id || '');
                  setToast({
                    open: true,
                    message: 'Copied!',
                    variant: 'success',
                  });
                }}
                style={{ cursor: 'pointer' }}
              >
                {documentData.id}
              </span>
            </TypographyPropertiesDataStyled>
          </div>
          {documentData.token_count ? (
            <>
              <DivVerticalDividerSmall />
              <div>
                <TypographyPropertiesTitleStyled>TOKENS</TypographyPropertiesTitleStyled>
                <TypographyPropertiesDataStyled>
                  {documentData.token_count.toLocaleString()}
                </TypographyPropertiesDataStyled>
              </div>
            </>
          ) : null}

          {documentData.format_id ? (
            <>
              <DivVerticalDividerSmall />
              <div>
                <TypographyPropertiesTitleStyled>FORMAT ID</TypographyPropertiesTitleStyled>
                <TypographyPropertiesDataStyled>
                  <CollapsedTitleWithTooltip
                    title={documentData.format_id}
                    charsFirstAndLast={5}
                    clickToCopy={true}
                  />
                  <Tooltip title={'Filter'} placement="top">
                    <Link
                      to={`/data-pools/${dpid}/documents?page=0&format_id=${documentData.format_id}`}
                      style={{ textDecoration: 'none' }}
                    >
                      <FilterAltIcon
                        style={{
                          color: theme.palette.custom.pageBackground,
                          cursor: 'pointer',
                          fontSize: '12px',
                          marginLeft: '5px',
                        }}
                      />{' '}
                    </Link>
                  </Tooltip>
                </TypographyPropertiesDataStyled>
              </div>
            </>
          ) : null}

          {showDuplicates ? (
            <>
              <DivVerticalDividerSmall />
              <div>
                <TypographyPropertiesTitleStyled>DUPLICATES</TypographyPropertiesTitleStyled>
                <TypographyPropertiesDataStyled>
                  <Link
                    to={`/data-pools/${dpid}/documents?page=0&in_relationship_set_with=${documentData.id}`}
                    style={{ textDecoration: 'none' }}
                  >
                    {' '}
                    {dupChildren.length > 0 ? dupChildren.length.toLocaleString() : 'duplicate'}
                  </Link>
                </TypographyPropertiesDataStyled>
              </div>
            </>
          ) : null}

          <DivVerticalDividerSmall />

          <div>
            <TypographyPropertiesTitleStyled>TYPE</TypographyPropertiesTitleStyled>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <TypographyPropertiesDataStyled>
                <Tooltip title={documentData.content_type} placement="top">
                  <span>{documentData.file_extension?.replace('.', '').toUpperCase()}</span>
                </Tooltip>
              </TypographyPropertiesDataStyled>
            </div>
          </div>

          {documentData.language ? (
            <>
              <DivVerticalDividerSmall />
              <div>
                <TypographyPropertiesTitleStyled>LANGUAGE</TypographyPropertiesTitleStyled>
                <TypographyPropertiesDataStyled>
                  {documentData.language.toUpperCase()}
                </TypographyPropertiesDataStyled>
              </div>
            </>
          ) : null}
          <DivVerticalDividerSmall />
          <div>
            <TypographyPropertiesTitleStyled>PAGES</TypographyPropertiesTitleStyled>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <TypographyPropertiesDataStyled>
                {documentData?.pages?.length}
              </TypographyPropertiesDataStyled>
            </div>
          </div>
          <DivVerticalDividerSmall />
          <div>
            <TypographyPropertiesTitleStyled>CREATED</TypographyPropertiesTitleStyled>
            <TypographyPropertiesDataStyled>
              {createdDate.toDateString()}
            </TypographyPropertiesDataStyled>
          </div>
          <DivVerticalDividerLarge />
          <div>
            <RerenderDocumentPages dpid={dpid!} documentId={documentData.id} />
          </div>
          <div>
            {(() => {
              const caution = splitCaution(documentData);
              return (
                <IconButtonWhiteBg
                  onClick={() => navigate(`/data-pools/${dpid}/documents/${documentId}/split`)}
                  id="btn-split-document"
                  cypressId="btn-split-document"
                  tooltip={caution ?? 'Split document'}
                  sx={{ mr: 1 }}
                >
                  <CallSplitIcon style={{ fontSize: '12px', color: 'black' }} />
                </IconButtonWhiteBg>
              );
            })()}
          </div>
          <div>
            <DownloadDocument
              documentId={documentData.id}
              documentContentType={documentData.content_type}
              documentLabel={documentData.label}
              documentFileExtension={documentData.file_extension}
              iconType="iconButtonWhiteBg"
            />
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: '15px',
          width: '100%',
        }}
      >
        <FullDocumentView />
      </div>
    </div>
  );
};

export default DocumentDetailsRight;
